import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import CatalogueTopNav from '../new-service/CatalogueTopNav';
import SubNav from './SubNav';
import VouchersPage from './VouchersPage';
import DesignsPage from './DesignsPage';
import SalesPage from './SalesPage';
import VoucherEditorPanel from './VoucherEditorPanel';
import VoucherDetailPanel from './VoucherDetailPanel';
import CustomDesignerPanel from './CustomDesignerPanel';
import VoucherOnboarding from './VoucherOnboarding';
import { DESIGNS, countAvailable } from './data';
import { giftCardService } from '../../_services/giftCard.service';
import { syncCatalogueAfterSave } from '../multiLocation/syncCatalogue';
import { mergeDesigns } from './giftCardMapper';
import { getGiftCardChannels, setGiftCardChannels } from './giftCardChannels';
import { can, getAccess } from '../permissions/access';
import './GiftCardsV2.css';

// The editorial/abstract library is a static FE asset (one per occasion).
// The backend persists only availability overrides + custom designs, which
// we merge onto this base. Drop the data.js demo custom seed (isCustom) —
// real customs come from the backend.
const BASE_DESIGNS = DESIGNS.filter((d) => !d.isCustom);

// Voucher V2 — entry component.
//
// Renders the standard catalogue chrome (header tabs + sub-nav + page
// container) and switches between three pages: Vouchers (the table),
// Designs (the library), and Sales (stub for phase 2).
//
// The designs library is lifted here so the SubNav `Designs` count chip
// stays in sync with the per-design toggles. Phase 2 will swap this
// useState for a Redux/persistence layer.

const PAGE_SUBTITLES = {
  vouchers: 'Products your clients can buy. Face values, promo pricing, channels, T&Cs and expiry.',
  designs:  'Your voucher design library. Manage which designs are available to assign to vouchers.',
  sales:    'Every voucher ever sold. Filter by design, status, channel and date.',
};

// Persisted flag so the "how it works" walkthrough auto-opens only on a
// salon's first visit to this page. The `?` button reopens it any time.
const ONBOARDING_KEY = 'beu_voucher_v2_onboarded';

const GiftCardsV2 = ({ user, salonImage, portfolio, location }) => {
  // Marketing › Performance links straight to Sales
  const [subNav, setSubNav] = useState(() => (location && location.state && location.state.subNav) || 'vouchers');

  // Reception mode: the Designs / Sales tabs can be hidden per the salon's
  // visibility settings — same source + event the catalogue nav uses.
  const readReceptionSettings = () => {
    try {
      const cached = localStorage.getItem('receptionModeSettings');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore parse errors */ }
    return { enabled: false, hiddenAreas: {} };
  };
  const [receptionSettings, setReceptionSettings] = useState(readReceptionSettings);
  useEffect(() => {
    const onChange = () => setReceptionSettings(readReceptionSettings());
    window.addEventListener('receptionModeSettingsChanged', onChange);
    return () => window.removeEventListener('receptionModeSettingsChanged', onChange);
  }, []);
  const receptionHiddenAreas = (receptionSettings && receptionSettings.enabled && receptionSettings.hiddenAreas) || {};
  // Sales (revenue + buyer details) needs `reports` — the server refuses the
  // sales list, dashboard and design stats without it (access audit 2026-10-02).
  const canSeeSales = can('reports');
  const visibleSubNav = ['vouchers']
    .concat(receptionHiddenAreas.voucherDesigns ? [] : ['designs'])
    .concat(receptionHiddenAreas.voucherSales || !canSeeSales ? [] : ['sales']);
  useEffect(() => {
    if (!visibleSubNav.includes(subNav)) setSubNav('vouchers');
  }, [visibleSubNav.join(','), subNav]); // eslint-disable-line react-hooks/exhaustive-deps

  const [designs, setDesigns] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  // Real per-design conversion stats { [designId]: {...} } for the design panel.
  const [designStats, setDesignStats] = useState({});

  // Editor state — `editingVoucher: null` closes; `'new'` opens for a
  // fresh voucher; a voucher object opens for editing that one.
  const [editingVoucher, setEditingVoucher] = useState(null);

  // Read-only view state — a row click opens this first; the panel's
  // "Edit voucher" button hands off to the editor (view → edit).
  const [viewingVoucher, setViewingVoucher] = useState(null);

  // Custom designer state is owned here (not inside DesignsPage) so the
  // top-nav "New custom design" action can open it from any sub-tab —
  // the modal renders at this level and overlays everything.
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [editingCustom, setEditingCustom] = useState(null);

  // "How it works" walkthrough — opened from the `?` button in the header,
  // and auto-opened once on a salon's first visit (see effect below).
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Salon-wide selling channels — a master override on top of each voucher's
  // own online/pos flags. Off here = that channel is hidden everywhere,
  // per-voucher settings preserved. Mock-first (see giftCardChannels.js).
  const [channels, setChannels] = useState(getGiftCardChannels);
  const handleSetChannel = (key, on) => {
    const next = { ...channels, [key]: on };
    setChannels(next);
    setGiftCardChannels(next);   // mock persist; backend swaps to the salon record
  };

  // First-visit auto-open. A short delay lets the page paint first so the
  // walkthrough animates in over real content rather than a blank loader.
  // The flag is re-checked at fire time so a manual open+close in the gap
  // (which sets the flag) cancels the auto-open.
  useEffect(() => {
    let seen = '1';
    try { seen = localStorage.getItem(ONBOARDING_KEY); } catch (e) { /* storage blocked */ }
    if (seen) return undefined;
    const t = setTimeout(() => {
      try { if (localStorage.getItem(ONBOARDING_KEY)) return; } catch (e) { /* ignore */ }
      setShowOnboarding(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  // Close the walkthrough and remember it's been seen so it won't auto-open
  // again. Used for both the auto-opened and `?`-opened cases.
  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch (e) { /* storage blocked */ }
  };

  const currency = user?.userInfo?.hairdresser?.currencySymbol || '£';
  const salonName = user?.userInfo?.hairdresser?.name || 'Your Salon';

  // Load vouchers, designs and sales from the backend on mount. The designs
  // list from the API is just overrides + customs — merge onto the static base.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Sales are NOT loaded here — the Sales tab fetches its own paginated
        // rows + server-side aggregates (it would be unbounded for big salons).
        const [v, persistedDesigns, statsRes] = await Promise.all([
          giftCardService.listVouchers(),
          giftCardService.listDesigns(),
          canSeeSales ? giftCardService.getDesignStats().catch(() => ({ stats: {} })) : Promise.resolve({ stats: {} }),
        ]);
        if (!active) return;
        setVouchers(v);
        setDesigns(mergeDesigns(BASE_DESIGNS, persistedDesigns));
        setDesignStats((statsRes && statsRes.stats) || {});
      } catch (e) {
        console.error('Failed to load gift cards', e);
        if (active) setDesigns(mergeDesigns(BASE_DESIGNS, []));
      }
    })();
    return () => { active = false; };
  }, []);

  // Open the editor for an existing voucher.
  const handleEdit = (voucher) => setEditingVoucher(voucher);

  // Open the editor for a new voucher (sentinel 'new' avoids any chance
  // of a falsy object being misread as 'closed').
  const handleNew = () => setEditingVoucher('new');

  // Persist the editor's output to the backend, then upsert the returned
  // (canonical, real-id) voucher into local state.
  const handleSaveVoucher = async (voucher) => {
    try {
      const { _locations, ...toSave } = voucher;
      const saved = await giftCardService.saveVoucher(toSave, currency);
      // Locations control: copy/update this voucher at the chosen sites.
      const catalogId = saved && (saved._id || saved.catalogId || saved.id);
      if (catalogId && _locations) syncCatalogueAfterSave('voucher', String(catalogId), _locations);
      setVouchers((prev) => {
        const exists = prev.some(v => v.id === saved.id);
        return exists ? prev.map(v => v.id === saved.id ? saved : v) : [...prev, saved];
      });
    } catch (e) {
      console.error('Failed to save voucher', e);
    }
    setEditingVoucher(null);
  };

  // Toggle a single design's library availability. `next` is optional —
  // when omitted, flip the current value. Bulk operations in DesignsPage
  // pass `next` explicitly so they're idempotent. Optimistic: flip locally,
  // persist the override; on failure revert.
  const handleToggleDesign = (id, next) => {
    const target = designs.find((d) => d.id === id);
    const value = typeof next === 'boolean' ? next : !(target && target.available);
    setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, available: value } : d)));
    giftCardService
      .toggleDesign(id, value, target ? { aesthetic: target.aesthetic, isCustom: !!target.isCustom } : {})
      .catch((e) => {
        console.error('Failed to toggle design', e);
        setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, available: !value } : d)));
      });
  };

  // Save (create or update) a custom design. Persist to the backend, then
  // upsert into local state by id.
  const handleSaveCustomDesign = async (design) => {
    setDesigns((prev) => {
      const exists = prev.some(d => d.id === design.id);
      return exists
        ? prev.map(d => d.id === design.id ? design : d)
        : [...prev, design];
    });
    try {
      await giftCardService.saveCustomDesign(design);
    } catch (e) {
      console.error('Failed to save custom design', e);
    }
  };

  // Resend the voucher email for a sale (instance id = sale.id).
  const handleResendEmail = async (sale) => {
    await giftCardService.resendEmail(sale.id);
  };

  // Email holders of vouchers expiring within `withinDays`.
  const handleNotifyExpiring = (withinDays) => giftCardService.notifyExpiring(withinDays);

  // Bind a client (buyer/recipient contact) to an unclaimed sale. The Sales
  // page refetches its own rows + aggregates after this resolves.
  const handleAttachClient = async (sale, role, contact) => {
    await giftCardService.attachClient({ voucherId: sale.id, role, ...contact });
  };

  // SubNav counts. Designs shows AVAILABLE / TOTAL so the salon sees at
  // a glance how loaded the library is.
  const counts = useMemo(() => ({
    vouchers: vouchers.length,
    designs: countAvailable(designs),
  }), [vouchers, designs]);

  return (
    <div className="h_n _new__service catalogue-no-header">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          <CatalogueTopNav>
            {/* Top-right Add dropdown — matches the Services tab pattern
                so the catalogue chrome reads consistently across tabs. */}
            <Dropdown className="catalogue-add-dropdown">
              <Dropdown.Toggle id="catalogue-add-vouchers-v2">
                <span>Add</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleNew}>New voucher</Dropdown.Item>
                <Dropdown.Item onClick={() => setCreatingCustom(true)}>New custom design</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </CatalogueTopNav>
          <div className="catalogue-content-container">
            <div className="catalogue-content-wrapper voucher-v2">

              <div className="v2-page-header">
                <div className="v2-page-title-row">
                  <h1 className="v2-page-title">Vouchers</h1>
                  <button
                    type="button"
                    className="v2-help-btn"
                    onClick={() => setShowOnboarding(true)}
                    aria-label="How vouchers work"
                    title="How vouchers work"
                  >
                    ?
                  </button>
                </div>
                <p className="v2-page-sub">{PAGE_SUBTITLES[subNav]}</p>
              </div>

              <SubNav active={subNav} onChange={setSubNav} counts={counts} visible={visibleSubNav} />

              <div className="v2-page">
                {subNav === 'vouchers' && (
                  <VouchersPage
                    vouchers={vouchers}
                    currency={currency}
                    channels={channels}
                    onSetChannel={handleSetChannel}
                    onView={setViewingVoucher}
                    onNew={handleNew}
                  />
                )}
                {subNav === 'designs' && (
                  <DesignsPage
                    designs={designs}
                    designStats={designStats}
                    onToggle={handleToggleDesign}
                    vouchers={vouchers}
                    onOpenVoucher={handleEdit}
                    salonName={salonName}
                    currency={currency}
                    onCreateCustom={() => setCreatingCustom(true)}
                    onEditCustom={setEditingCustom}
                  />
                )}
                {subNav === 'sales'   && (
                  <SalesPage
                    vouchers={vouchers}
                    designs={designs}
                    currency={currency}
                    salonName={salonName}
                    onResendEmail={handleResendEmail}
                    // Emailing every holder is marketing — owner only.
                    onNotifyExpiring={getAccess().owner ? handleNotifyExpiring : undefined}
                    onAttachClient={handleAttachClient}
                  />
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Voucher detail — read-only view opened by a row click. Its
          "Edit voucher" button transitions to the editor (view → edit). */}
      {viewingVoucher && (
        <VoucherDetailPanel
          voucher={viewingVoucher}
          designs={designs}
          currency={currency}
          salonName={salonName}
          onClose={() => setViewingVoucher(null)}
          onEdit={(v) => { setViewingVoucher(null); setEditingVoucher(v); }}
        />
      )}

      {/* Voucher editor — slides in from the right. `editingVoucher`
          carries the voucher object (existing edit) or the string 'new'
          (fresh voucher); the panel reads it to seed its form state. */}
      {editingVoucher && (
        <VoucherEditorPanel
          voucher={editingVoucher === 'new' ? null : editingVoucher}
          designs={designs}
          currency={currency}
          salonName={salonName}
          channels={channels}
          onClose={() => setEditingVoucher(null)}
          onSave={handleSaveVoucher}
        />
      )}

      {/* "How it works" walkthrough — centred pop-up on web, slide-up sheet
          on mobile. Explains the designs → vouchers → sell → spend flow. */}
      {showOnboarding && (
        <VoucherOnboarding
          salonName={salonName}
          currency={currency}
          onClose={handleCloseOnboarding}
        />
      )}

      {/* Custom designer — hoisted here so it can be opened from the
          top-nav Add dropdown regardless of which sub-tab is active. */}
      {(editingCustom || creatingCustom) && (
        <CustomDesignerPanel
          design={editingCustom}
          existingDesigns={designs}
          onClose={() => { setEditingCustom(null); setCreatingCustom(false); }}
          onSave={handleSaveCustomDesign}
          salonName={salonName}
          currency={currency}
          salonImage={salonImage}
          portfolio={portfolio}
        />
      )}
    </div>
  );
};

// Beu stores portfolio + the salon's main image in a couple of different
// state shapes depending on which fetch populated them. The custom
// designer's Image panel only reads them — so we collapse the lookups
// here with defensive fallbacks rather than threading slice picks down.
const mapStateToProps = (state) => ({
  user: state.user,
  salonImage:
    state.user?.userInfo?.hairdresser?.image
    || state.salon?.salonInfo?.salons?.image
    || null,
  portfolio:
    state.user?.userInfo?.hairdresser?.portfolio
    || state.hairdresser?.userInfo?.hairdresser?.portfolio
    || [],
});
export default connect(mapStateToProps)(GiftCardsV2);
